/**
 * Lightweight API client for AH Home.
 *
 * Reuses the legacy localStorage JWT when present, but always sends
 * browser cookies so the backend can restore persistent AH Home sessions.
 * Redirects to /login on 401 only when auth is actually required.
 */

import { resolveApiUrl } from './config';
import { getAuthStatus } from '../auth/status';

const AUTH_TOKEN_KEY = 'ah-auth-token';

class ApiClient {
  private authToken?: string;

  constructor() {
    this.authToken = localStorage.getItem(AUTH_TOKEN_KEY) || undefined;
  }

  resolveUrl(endpoint: string): string {
    return resolveApiUrl(endpoint);
  }

  private getErrorDetail(body: string): string | null {
    try {
      const parsed = JSON.parse(body) as { detail?: unknown };
      return typeof parsed.detail === 'string' ? parsed.detail : null;
    } catch {
      return null;
    }
  }

  private async shouldRedirectToLogin(endpoint: string, body: string): Promise<boolean> {
    const authStatus = getAuthStatus();
    if (!authStatus.authRequired || authStatus.authDisabled) {
      return false;
    }

    if (endpoint.startsWith('/api/auth/')) {
      return true;
    }

    const detail = this.getErrorDetail(body);
    if (detail === 'Not authenticated' || detail === 'Session user does not match request user') {
      return true;
    }

    try {
      const meResp = await fetch(this.resolveUrl('/api/auth/me'), {
        credentials: 'include',
        headers: this.getAuthHeaders(),
      });
      return meResp.status === 401;
    } catch {
      return false;
    }
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = this.resolveUrl(endpoint);
    const config: RequestInit = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(this.authToken && { Authorization: `Bearer ${this.authToken}` }),
        ...options.headers,
      },
      ...options,
    };

    let response: Response;
    try {
      response = await fetch(url, config);
    } catch (err) {
      throw new Error(
        `Network error: Unable to reach backend${err instanceof TypeError ? ` (${err.message})` : ''}`
      );
    }

    if (response.status === 401) {
      const body = await response.text();
      if (await this.shouldRedirectToLogin(endpoint, body)) {
        this.clearAuthToken();
        window.location.href = '/login';
        throw new Error('Authentication expired. Redirecting to login.');
      }
      throw new Error(`HTTP 401: ${this.getErrorDetail(body) ?? body}`);
    }

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`HTTP ${response.status}: ${this.getErrorDetail(body) ?? body}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  getAuthToken(): string | undefined {
    return this.authToken;
  }

  getAuthHeaders(): HeadersInit | undefined {
    return this.authToken
      ? { Authorization: `Bearer ${this.authToken}` }
      : undefined;
  }

  setAuthToken(token: string): void {
    this.authToken = token;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }

  clearAuthToken(): void {
    this.authToken = undefined;
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

const apiClient = new ApiClient();
export default apiClient;
